import React from "react";
import { cn } from "@/lib/utils";
import { useI18n } from "@/context/I18nContext";
import { AlertTriangle, Clock, ShieldCheck, XCircle, FileText, FileSignature, Mail, Files, Landmark, Gavel, Stamp } from "lucide-react";

export const urgencyStyles = {
  expired: "bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-950 dark:text-rose-200 dark:border-rose-800",
  critical: "bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950 dark:text-amber-200 dark:border-amber-800",
  warning: "bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-950 dark:text-yellow-200 dark:border-yellow-800",
  safe: "bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-950 dark:text-emerald-200 dark:border-emerald-800",
  none: "bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700",
};

export const urgencyBorder = {
  expired: "border-l-rose-600",
  critical: "border-l-amber-500",
  warning: "border-l-yellow-400",
  safe: "border-l-emerald-500",
  none: "border-l-slate-300",
};

const urgencyIcons = {
  expired: XCircle,
  critical: AlertTriangle,
  warning: Clock,
  safe: ShieldCheck,
  none: Clock,
};

export function UrgencyBadge({ urgency, testId }) {
  const { t } = useI18n();
  const Icon = urgencyIcons[urgency] || Clock;
  const labels = { expired: t("expired"), critical: t("critical"), warning: t("warning"), safe: t("safe"), none: t("none") };
  return (
    <span
      data-testid={testId}
      className={cn("inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold", urgencyStyles[urgency] || urgencyStyles.none)}
    >
      <Icon className="h-3 w-3" />
      {labels[urgency] || labels.none}
    </span>
  );
}

export const typeIcons = { statutory: Landmark, license: FileText, contract: FileSignature, litigation: Gavel, resolutions: Stamp, correspondence: Mail, other: Files };

export function TypeBadge({ type, testId }) {
  const { t } = useI18n();
  const Icon = typeIcons[type] || FileText;
  return (
    <span data-testid={testId} className="inline-flex items-center gap-1.5 rounded-md bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
      <Icon className="h-3 w-3" />
      {t(type)}
    </span>
  );
}

export function StatusBadge({ status }) {
  const { t } = useI18n();
  const map = {
    active: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/50 dark:text-emerald-300",
    pending: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/50 dark:text-blue-300",
    renewed: "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/50 dark:text-violet-300",
    expired: "bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-950/50 dark:text-rose-300",
  };
  return (
    <span className={cn("inline-flex rounded-md border px-2 py-0.5 text-xs font-medium capitalize", map[status] || map.active)}>
      {t(status) || status}
    </span>
  );
}
